import React, { useEffect, useMemo, useState } from "react";
import { useParams, useHistory } from "react-router";
import { Segmented, Select, Skeleton, Empty, Tag, Button, Tooltip } from "antd";
import {
  BarChartOutlined,
  HeatMapOutlined,
  ArrowLeftOutlined,
  FileZipOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ProjectOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { Link } from "react-router-dom";
import { issuePath } from "helpers/issueLinks";
import {
  getUserOverview,
  getUserContributions,
} from "api/analytics/projectAnalyticsApi";

const T = {
  ink: "#23343E", text: "#3A4952", muted: "#8D8D8D", sub: "#AEAEAE",
  line: "#EFEFEF", line2: "#E2E2E2", bg: "#FAFAFA", wash: "#E9F1F5",
  navy: "#455D6B", navyD: "#3A5765",
  added2: "#317A1B", removed2: "#C53030", amber: "#E0A43A",
  purple: "#5765D4",
};

function Card({ title, extra, children, style }) {
  return (
    <div style={{
      background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8,
      padding: 16, ...style,
    }}>
      {(title || extra) && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          {title && <div style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{title}</div>}
          <span style={{ flex: 1 }} />
          {extra}
        </div>
      )}
      {children}
    </div>
  );
}

function Stat({ label, value, color = T.ink, icon }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, color: T.muted, fontWeight: 600, letterSpacing: ".06em", marginBottom: 4 }}>
        {label.toUpperCase()}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color, display: "flex", alignItems: "center", gap: 8 }}>
        {icon}{value}
      </div>
    </div>
  );
}

function BarsRow({ days, daily, maxDay, height = 60 }) {
  const peak = Math.max(1, maxDay);
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(${days.length}, 1fr)`,
      gap: 1, height, alignItems: "end",
    }}>
      {days.map(d => {
        const count = (daily[d] && daily[d].total) || 0;
        const h = count ? Math.max(3, (count / peak) * height) : 2;
        return (
          <Tooltip key={d} title={`${moment(d).format("MMM D")} — ${count} event${count === 1 ? "" : "s"}`}>
            <div style={{
              height: h, background: count ? T.purple : T.line2,
              opacity: count ? Math.max(0.3, count / peak) : 1,
              borderRadius: 1,
            }} />
          </Tooltip>
        );
      })}
    </div>
  );
}

function HeatmapGrid({ days, daily, maxDay }) {
  const peak = Math.max(1, maxDay);
  const weeks = [];
  let currentWeek = new Array(7).fill(null);
  days.forEach(d => {
    const dow = moment(d).day();
    if (dow === 0 && currentWeek.some(Boolean)) {
      weeks.push(currentWeek);
      currentWeek = new Array(7).fill(null);
    }
    currentWeek[dow] = d;
  });
  if (currentWeek.some(Boolean)) weeks.push(currentWeek);

  const colorFor = (count) => {
    if (!count) return "#ECEEF3";
    const t = count / peak;
    if (t < 0.25) return "#D4D8F2";
    if (t < 0.5) return "#A7AFEA";
    if (t < 0.75) return "#8E98E4";
    return T.purple;
  };

  return (
    <div style={{ display: "flex", gap: 2 }}>
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {week.map((d, di) => {
            if (!d) return <div key={di} style={{ width: 12, height: 12 }} />;
            const count = (daily[d] && daily[d].total) || 0;
            return (
              <Tooltip key={di} title={`${moment(d).format("ddd MMM D")} — ${count}`}>
                <div style={{
                  width: 12, height: 12, borderRadius: 2,
                  background: colorFor(count),
                }} />
              </Tooltip>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export default function UserAnalyticsPage() {
  const { userId } = useParams();
  const history = useHistory();
  const [overview, setOverview] = useState(null);
  const [contrib, setContrib] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(90);
  const [chartStyle, setChartStyle] = useState("heatmap");

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    Promise.all([
      getUserOverview(userId),
      getUserContributions(userId, days),
    ])
      .then(([o, c]) => { if (!cancelled) { setOverview(o); setContrib(c); } })
      .catch(e => !cancelled && setErr(e.message || "Failed to load"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [userId, days]);

  const user = overview?.user;
  const stats = overview?.stats || {};

  if (loading) {
    return (
      <div style={{ padding: "24px 32px", background: T.bg, minHeight: "calc(100 * var(--vh))" }}>
        <Skeleton active avatar paragraph={{ rows: 8 }} />
      </div>
    );
  }
  if (err) {
    return (
      <div style={{ padding: "24px 32px", background: T.bg, minHeight: "calc(100 * var(--vh))" }}>
        <div style={{
          padding: 16, background: "#FFF0F0", border: "1px solid #FAD6D6",
          borderRadius: 6, color: T.removed2,
        }}>{err}</div>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 32px", background: T.bg, minHeight: "calc(100 * var(--vh))" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => history.goBack()} />
        <div style={{
          width: 56, height: 56, borderRadius: "50%", background: T.purple,
          color: "#fff", fontSize: 22, fontWeight: 600,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          {(user?.name || "?").charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.ink }}>{user?.name}</div>
          <div style={{ fontSize: 12, color: T.muted }}>
            {user?.email}
            {user?.role ? ` · ${user.role}` : ""}
            {overview?.projectCount != null ? ` · ${overview.projectCount} project${overview.projectCount === 1 ? "" : "s"}` : ""}
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 16,
      }}>
        <Card>
          <Stat label="Issues created" value={stats.issuesCreated || 0}
                icon={<FileZipOutlined style={{ color: T.navy, fontSize: 18 }} />} />
        </Card>
        <Card>
          <Stat label="Issues resolved" value={stats.issuesResolved || 0} color={T.added2}
                icon={<CheckCircleOutlined style={{ fontSize: 18 }} />} />
        </Card>
        <Card>
          <Stat label="Open assigned" value={stats.assignedOpen || 0} color="#B45309"
                icon={<WarningOutlined style={{ fontSize: 18 }} />} />
        </Card>
        <Card>
          <Stat label="Exports authored" value={stats.exportsByUser || 0} />
        </Card>
        <Card>
          <Stat label="Events (30d)" value={stats.activityLast30Days || 0} color={T.purple} />
        </Card>
      </div>

      {/* Activity */}
      <Card
        title={<>
          <span>Daily contributions</span>
          <span style={{ fontSize: 11, color: T.muted, marginLeft: 8, fontWeight: 400 }}>
            {contrib?.total || 0} events · active {contrib?.activeDays || 0}/{(contrib?.days || []).length} days
          </span>
        </>}
        extra={
          <div style={{ display: "flex", gap: 8 }}>
            <Select size="small" style={{ width: 110 }}
              value={days} onChange={setDays}
              options={[
                { value: 30, label: "30 days" },
                { value: 60, label: "60 days" },
                { value: 90, label: "90 days" },
                { value: 180, label: "180 days" },
              ]}
            />
            <Segmented size="small" value={chartStyle} onChange={setChartStyle}
              options={[
                { value: "bars", icon: <BarChartOutlined /> },
                { value: "heatmap", icon: <HeatMapOutlined /> },
              ]}
            />
          </div>
        }
        style={{ marginBottom: 16 }}
      >
        {(contrib?.days || []).length === 0 ? (
          <Empty description="No activity in range" />
        ) : chartStyle === "bars" ? (
          <BarsRow days={contrib.days} daily={contrib.daily} maxDay={contrib.maxDay || 1} />
        ) : (
          <HeatmapGrid days={contrib.days} daily={contrib.daily} maxDay={contrib.maxDay || 1} />
        )}
      </Card>

      {/* Lists */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
      }}>
        <Card title="Recent issues (created / assigned)">
          {(overview?.recentIssues || []).length === 0 ? (
            <Empty description="No recent issues" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {overview.recentIssues.map(m => (
                <div key={m.id}
                  onClick={() => history.push(issuePath(m))}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", background: T.bg, borderRadius: 6, cursor: "pointer",
                  }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.label && <span style={{ fontFamily: "monospace", fontSize: 11, color: T.muted, marginRight: 6 }}>{m.label}</span>}
                      {m.title}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted }}>
                      {moment(m.updatedAt).fromNow()}
                      {m.projectTitle ? ` · ${m.projectTitle}` : ""}
                    </div>
                  </div>
                  <Tag color={
                    m.status === "resolved" ? "green" :
                    m.status === "pending" ? "orange" :
                    m.status === "abandoned" ? "default" : "blue"
                  } style={{ margin: 0, fontSize: 10 }}>
                    {(m.status || "open").toUpperCase()}
                  </Tag>
                  <Link to={issuePath(m)} onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: 10.5, fontWeight: 600, color: T.navyD, border: "1px solid #D4E3EC", background: T.wash, borderRadius: 4, padding: "2px 8px", whiteSpace: "nowrap" }}>
                    View issue
                  </Link>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Recent exports">
          {(overview?.recentExports || []).length === 0 ? (
            <Empty description="No recent exports" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {overview.recentExports.map(e => (
                <div key={e.id} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 10px", background: T.bg, borderRadius: 6,
                }}>
                  <FileZipOutlined style={{ color: T.navy }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {e.title || "Export"}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted }}>
                      {moment(e.date).fromNow()}
                      {e.milestone ? ` · ${e.milestone}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Projects */}
      {overview?.projects?.length > 0 && (
        <Card title="Projects involved" style={{ marginTop: 16 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {overview.projects.map(p => (
              <div key={p._id}
                onClick={() => history.push(`/analytics/project/${p._id}`)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 10px", background: T.wash, borderRadius: 4,
                  fontSize: 12, color: T.navyD, fontWeight: 500, cursor: "pointer",
                }}>
                <ProjectOutlined /> {p.title}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
